/**
 * Status presentation for the admin console.
 *
 * These maps were repeated at the top of every admin screen, which is how the
 * same status ended up amber on one page and grey on the next. An admin reads
 * colour before text, so one table for the whole console is not tidiness --
 * it is the difference between a badge meaning something and meaning nothing.
 *
 * Mirrors the enums in app/core/constants.py.
 */

export const ACCOUNT_TONES = {
  ACTIVE: 'success',
  PENDING_VERIFICATION: 'warning',
  INACTIVE: 'neutral',
  SUSPENDED: 'danger',
  BANNED: 'danger',
  DELETED: 'neutral',
};

export const VERIFICATION_TONES = {
  PENDING: 'warning',
  VERIFIED: 'success',
  REJECTED: 'danger',
  UNVERIFIED: 'neutral',
};

export const INTERNSHIP_TONES = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  PUBLISHED: 'success',
  REJECTED: 'danger',
  CLOSED: 'neutral',
  ARCHIVED: 'neutral',
};

/** A workspace seat. `INVITED` is a seat nobody has taken up yet. */
export const MEMBER_TONES = {
  ACTIVE: 'success',
  INVITED: 'warning',
  REMOVED: 'neutral',
};

/**
 * Workspace roles, spelled the way a person would say them.
 *
 * `humanizeStatus` would render `HR` as "Hr", and this is the one enum in the
 * console with an initialism in it.
 */
export const COMPANY_ROLE_LABELS = {
  COMPANY_ADMIN: 'Company admin',
  HR: 'HR',
  RECRUITER: 'Recruiter',
  HIRING_MANAGER: 'Hiring manager',
};

export function roleLabel(value) {
  return COMPANY_ROLE_LABELS[value] ?? humanizeStatus(value);
}

/**
 * A report is open until somebody decides it, and the two ways of deciding are
 * not the same answer -- resolved means the content was acted on, dismissed
 * means it was looked at and was fine. They are coloured apart for that reason:
 * a queue where both read as "closed" cannot show whether reporting is working.
 */
export const REPORT_TONES = {
  OPEN: 'warning',
  RESOLVED: 'success',
  DISMISSED: 'neutral',
};

export const REPORT_TARGET_LABELS = {
  INTERNSHIP: 'Internship',
  COMPANY: 'Company',
  STUDENT: 'Student',
};

export const SUBSCRIPTION_TONES = {
  ACTIVE: 'success',
  TRIALING: 'info',
  PAST_DUE: 'warning',
  CANCELED: 'neutral',
};

/**
 * `PENDING_APPROVAL` -> `Pending approval`.
 *
 * Shouting a constant at the reader is a leaked implementation detail, not a
 * label; every status in the console goes through here.
 */
export function humanizeStatus(value) {
  if (!value) return '—';
  const words = String(value).replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The tone for an audit action: an admin's own action stands out in the
 * primary colour, a failure or rejection is danger, everything else is
 * neutral. Withdrawn and deleted are neutral too -- the same reading they get
 * on the records themselves.
 */
export function auditActionTone(action = '') {
  if (action.startsWith('admin.')) return 'brand';
  if (action.includes('failed') || action.includes('rejected')) return 'danger';
  return 'neutral';
}
